import React, { useEffect, useState } from 'react';
import { Segment, Form, Input, Loader, Grid, Image, Popup } from 'semantic-ui-react';
import { Button, Notification, STATUS_TYPES } from 'dyl-components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import authActions from 'actions/auth/auth';
import basePath from 'actions/basePath';
import './index.scss';
import usersActions from 'actions/users';

const Login = ({ onLogin, onChange, isAuthenticatingUser, isSimulatingUser, isRedirectingToSales, isCapsLockOn, checkCapsLock }) => {
    if (isRedirectingToSales) {
        return (
            <Loader size='large' active>Redirecting to sales...</Loader> 
        )
    }
    if (isSimulatingUser) {
        return (
            <Loader size='large' active>Simulating user...</Loader> 
        )
    }
    return (
        <Grid columns="equal" stackable reversed="mobile">
            <Grid.Column style={{ padding: 0 }}>
                <Image src="https://images.dyl.com/login/dyl_logo.svg" className='Login__Logo' fluid alt="DYL Logo"/>
                <Image src="https://images.dyl.com/login/dyl_login.png" className='Login__Image' fluid alt="DYL Login"/>
            </Grid.Column>
            <Grid.Column style={{padding: 0}}>
                <div className="LoginWrapper">
                    <Segment className='Login' loading={ isAuthenticatingUser }>
                        <h1 className="Login__header">Welcome back to DYL!</h1>
                        <div className="Login__content">
                            <Form onSubmit={ onLogin }>
                                <div className="Login__fields">
                                    <h3 className="Login__fields-text">Customer Login</h3>
                                    <Form.Field>
                                        <Input 
                                            icon='fa-solid fa-envelope' 
                                            iconPosition='left' 
                                            placeholder='Email Address' 
                                            onChange={ onChange } 
                                            name='username' 
                                            fluid 
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <Popup
                                            trigger={
                                                <Input 
                                                    icon='lock' 
                                                    iconPosition='left' 
                                                    placeholder='Password' 
                                                    onChange={ onChange } 
                                                    name='password' 
                                                    type="password" 
                                                    fluid 
                                                    onKeyUp={checkCapsLock}
                                                    onMouseDown={checkCapsLock}
                                                />
                                            }
                                            content="Caps Lock On"
                                            inverted
                                            position='top left'
                                            open={isCapsLockOn}
                                        />
                                    </Form.Field>
                                    {/* <Form.Field>
                                        <Checkbox label='Remember me' onChange={ onChange } name='remember_me' />
                                    </Form.Field> */}
                                </div>
                                <div className="Login__Button">
                                    <Button type="submit" color='blue' style={{width: '35%'}}>Login</Button>
                                </div>
                            </Form>
                            <div className="Login__footer">
                                <a href='/forgot_password'>Forgot password?</a>
                            </div>
                            <div className="Login__Demo">
                                Don't have an account? <a href='https://dyl.com/free-consultation' target='_blank' rel='noreferrer'>Schedule a Demo</a>
                            </div>
                            <div className="Login__Privacy">
                                <a href='https://dyl.com/privacy-policy' target='_blank' rel='noreferrer'>Privacy & Policy</a> | <a href='https://dyl.com/terms-of-service' target='_blank' rel='noreferrer'>Terms</a>
                            </div>
                        </div>
                    </Segment>
                </div>
            </Grid.Column>
        </Grid>
    );
}


const LoginContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [form, setForm] = useState({ form: { username: '', password: '', remember_me: false } });
    const [firstLoad, setFirstLoad] = useState(true);
    const [isCheckingLogin, setIsCheckingLogin] = useState(false);
    const [isSimulatingUser, setIsSimulatingUser] = useState(false);
    const {isAuthenticatingUser, authenticateUserMessage, temporary_password, sales_redirect_token } = useSelector(state => state.auth);
    const [searchParams] = useSearchParams();
    const simulateToken = searchParams.get('simulate_token');
    const salesRedirectToken = searchParams.get('sales_redirect_token');
    const [isCapsLockOn, setIsCapsLockOn] = useState(false);

    const checkCapsLock = (event) => {
        if (typeof event.getModifierState === 'function') {
            const capsLockState = event.getModifierState('CapsLock');
            setIsCapsLockOn(capsLockState);
        }
    }

    const runLogin = async (form) => {
        const { username, password } = form;
        setFirstLoad(false);
        const ip_addr = await authActions.getIP();
        dispatch(authActions.login({ username, password, ip_addr }))
            .catch(e => {
                console.log(e);
                let message;
                if(e?.message === "missing username"){
                    message = "Missing email address";
                } else if(e?.message === "missing password") {
                    message = "Missing password";
                } 
                Notification.alert(message || 'Email or password is incorrect', STATUS_TYPES.ERROR);
            });
    };

    const onChange = (e) => {
        const inputValues = form;
        inputValues[e.target.name] = e.target.value;
        setForm(inputValues);
    };

    const onLogin = (e) => {
        e.preventDefault();
        runLogin(form);
    };

    useEffect(() => {
        if(!firstLoad){
            setIsCheckingLogin(isAuthenticatingUser)
        }
        return () => {
            setIsCheckingLogin(false)
        }
    }, [firstLoad, isAuthenticatingUser])

    useEffect(() => {
        if(simulateToken){
            setIsSimulatingUser(true);
            dispatch(authActions.login({ username: 'simulate', password: 'simulate', simulate_token: simulateToken }))
                .then(() => {
                    navigate('/');
                })
                .catch((e) => {
                    console.log(e);
                    Notification.alert('Failed to simulate user', STATUS_TYPES.ERROR);
                    navigate('/login');
                })
                .finally(() => {
                    setIsSimulatingUser(false);
                });
        }
    }, [simulateToken, dispatch, navigate])

    useEffect(() => {
        if (salesRedirectToken) {
            dispatch(authActions.login({ username: 'sales_redirect', password: 'sales_redirect', sales_redirect_token: salesRedirectToken }))
                .then(() => {
                    navigate('/');
                })
                .catch((e) => {
                    console.log(e);
                    Notification.alert('Failed to login', STATUS_TYPES.ERROR);
                    navigate('/login');
                })
        }
    }, [salesRedirectToken, dispatch, navigate])

    if (authenticateUserMessage === 'success') {
        if (!sales_redirect_token) {
            dispatch(usersActions.readUserProfile());
            let link = (location?.state?.from) ? location?.state?.from : '/';
            if(temporary_password){
                link = '/password_reset';
            }
            navigate(link);
            return null;
        } else {
            window.open(`${basePath.SALES_HOST}/login?sales_redirect_token=${sales_redirect_token}`, '_self', 'noopener,noreferrer');
            return null;
        }   
    }
    
    return (
        <Login 
            isRedirectingToSales={ salesRedirectToken } 
            isSimulatingUser={ isSimulatingUser || simulateToken } 
            isAuthenticatingUser={ isCheckingLogin } onChange={ onChange } 
            onLogin={ onLogin } 
            isCapsLockOn={isCapsLockOn}
            checkCapsLock={checkCapsLock}
        />
    );


}

export default LoginContainer
